import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { getFriendlyAuthErrorMessage } from "@/lib/authErrorMessages";

export default function AcceptInvitePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<any>(null);
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Supabase processes the #access_token hash automatically and fires auth state change
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, sess) => {
        if (sess) {
          setSession(sess);
          setLoading(false);
        }
      }
    );

    let timer: ReturnType<typeof setTimeout>;

    // Also check for an existing session (hash may already have been consumed)
    supabase.auth.getSession().then(({ data: { session: existing } }) => {
      if (existing) {
        setSession(existing);
        setLoading(false);
      } else {
        // Allow a few seconds for the hash exchange to complete
        timer = setTimeout(() => setLoading(false), 4000);
      }
    });

    return () => {
      subscription.unsubscribe();
      clearTimeout(timer);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!session?.user) return;

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setSubmitting(true);
    try {
      // 1. Set the user's password
      const { error: pwError } = await supabase.auth.updateUser({ password });
      if (pwError) throw pwError;

      // 2. Update profile display name
      const name = displayName.trim();
      if (name) {
        await supabase.from("profiles").upsert(
          { user_id: session.user.id, display_name: name },
          { onConflict: "user_id" }
        );
        await supabase.auth.updateUser({
          data: { display_name: name, full_name: name },
        });
      }

      // 3. Accept the invitation (links user to church + assigns role)
      const invToken = session.user.user_metadata?.invitation_token;
      if (invToken) {
        const { error: rpcError } = await supabase.rpc("accept_invitation" as any, {
          _token: invToken,
          _user_id: session.user.id,
        });
        if (rpcError) throw rpcError;
      }

      // Invalidate cached queries so ChurchProvider & SubscriptionProvider pick up the new role
      await queryClient.invalidateQueries({ queryKey: ["church_profile"] });
      await queryClient.invalidateQueries({ queryKey: ["user_role_church"] });
      await queryClient.invalidateQueries({ queryKey: ["subscription"] });

      
      navigate("/dashboard", { replace: true });
    } catch (err: any) {
      toast.error(getFriendlyAuthErrorMessage(err, "Failed to complete setup"));
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!session?.user) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <div className="text-center max-w-sm">
          <AlertTriangle className="w-12 h-12 mx-auto text-destructive mb-4" />
          <h1 className="text-xl font-heading font-bold text-foreground">Invalid or Expired Link</h1>
          <p className="text-muted-foreground mt-2">This invitation link is invalid or has expired.</p>
          <Button className="mt-6" onClick={() => navigate("/login")}>Go to Login</Button>
        </div>
      </div>
    );
  }

  const meta = session.user.user_metadata || {};
  const roleName = meta.role_name || meta.role || "Team Member";

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/ichurch-logo.svg" alt="iChurch Data" className="w-14 h-14 rounded-2xl object-cover mx-auto mb-4 shadow-md shadow-[#1e3a8a]/20" />
          <h1 className="text-2xl font-heading font-bold text-foreground">Complete Your Account</h1>
          <p className="text-muted-foreground mt-1">
            Set up your name and password to get started as <strong>{roleName}</strong>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="stat-card space-y-4">
          <div className="space-y-2">
            <Label>Email</Label>
            <Input
              type="email"
              value={session.user.email || ""}
              readOnly
              className="bg-muted text-muted-foreground cursor-not-allowed"
            />
          </div>
          <div className="space-y-2">
            <Label>Your Name</Label>
            <Input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Enter your name"
              required
            />
          </div>
          <div className="space-y-2">
            <Label>Create Password</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              minLength={6}
            />
          </div>
          <div className="space-y-2">
            <Label>Confirm Password</Label>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              required
              minLength={6}
            />
          </div>
          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle2 className="w-4 h-4 mr-2" />
            )}
            Complete Setup
          </Button>
        </form>
      </div>
    </div>
  );
}
